import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

const BASE_URL = import.meta.env.VITE_API_URL;

// Create a mutex to prevent multiple refresh attempts
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    // Wait for any ongoing refresh to complete
    await mutex.waitForUnlock();
    
    let result = await baseQuery(args, api, extraOptions);
    
    // Handle 401 errors with refresh token logic
    if (result.error && result.error.status === 401) {
      // Check if another request is already refreshing
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        
        try {
          // Try to refresh the session
          const refreshResult = await baseQuery(
            '/accounts/refreshSession',
            api,
            extraOptions
          );
          
          if (refreshResult.data) {
            // Session refreshed successfully, retry the original request
            result = await baseQuery(args, api, extraOptions);
          } else {
            // Refresh failed - dispatch logout action or handle session expiry
            // You could dispatch an action here to clear user state
            api.dispatch({ type: 'user/clearSession' });
          }
        } finally {
          release();
        }
      } else {
        // Wait for the ongoing refresh to complete, then retry
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }
    
    return result;
};

export const rootApi = createApi({
    reducerPath: 'rootApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Season', 'Teams', 'Injuries', 'DepthCharts', 'UpcomingGames', 
                'StadiumOrientations', 'GameScores', 'Systems', 'User'],
    endpoints: () => ({}),
});